import { useEffect, useRef, useState, type RefObject } from 'react'

export const transmissionPlaybackSpeeds = [1, 2, 3, 4, 5, 10, 20] as const

type Options = {
  videoRef: RefObject<HTMLVideoElement | null>
  source: string
  speed: number
  paused: boolean
  isPaused: () => boolean
  isSwitching: () => boolean
  advance: (offsetSeconds: number) => void
}

export function useTransmissionPlaybackSpeed(options: Options) {
  const latest = useRef(options)
  latest.current = options
  const [stepped, setStepped] = useState(false)
  const { videoRef, source, speed, paused } = options

  useEffect(() => {
    const video = videoRef.current
    if (!video || !source) { setStepped(false); return }
    let fallback = false
    let lastTick = performance.now()
    let anchorPosition = video.currentTime
    let requestedPosition = Number.NaN
    const originalMuted = video.muted
    const apply = () => {
      fallback = false
      try {
        video.playbackRate = speed
        fallback = Math.abs(video.playbackRate - speed) > .01
      } catch { fallback = true }
      // Chromium rejects rates above 16. Use explicit, silent fast-forward
      // instead of displaying 20x while the video still plays at its old rate.
      if (fallback) video.playbackRate = 1
      video.muted = fallback || originalMuted
      setStepped(fallback)
      if (latest.current.isPaused()) video.pause()
      resetClock()
    }
    const resetClock = () => { lastTick = performance.now(); anchorPosition = video.currentTime }
    const hasBufferedFrame = () => {
      const moment = video.currentTime
      for (let index = 0; index < video.buffered.length; index++) {
        if (video.buffered.start(index) <= moment + .02 && video.buffered.end(index) > moment + .02) return true
      }
      return false
    }
    const waiting = () => {
      // Seeking inside already buffered media spends time decoding on phones.
      // Preserve that elapsed time; exclude genuine waits for the network.
      if (!hasBufferedFrame()) resetClock()
    }
    const seeking = () => {
      // A manual timeline seek starts a new clock; our own pending seek keeps
      // accounting for the time the phone spends decoding its requested frame.
      if (Math.abs(video.currentTime - requestedPosition) > .2 || !Number.isFinite(requestedPosition)) resetClock()
    }
    apply()
    video.addEventListener('loadedmetadata', apply)
    video.addEventListener('play', apply)
    video.addEventListener('waiting', waiting)
    video.addEventListener('seeking', seeking)
    document.addEventListener('visibilitychange', resetClock)
    const timer = window.setInterval(() => {
      const now = performance.now()
      if (!fallback || latest.current.isPaused() || video.paused || document.hidden || latest.current.isSwitching()) {
        resetClock(); return
      }
      if (!hasBufferedFrame()) { resetClock(); return }
      if (video.seeking || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return
      const elapsed = Math.min(5, Math.max(0, (now - lastTick) / 1000))
      requestedPosition = anchorPosition + elapsed * speed
      latest.current.advance(requestedPosition)
      lastTick = now
      // Anchor to the accepted seek, so decode time counts exactly once and
      // rejected seeks at a growing playlist boundary do not accumulate debt.
      anchorPosition = video.currentTime
    }, 250)
    return () => {
      window.clearInterval(timer)
      video.removeEventListener('loadedmetadata', apply)
      video.removeEventListener('play', apply)
      video.removeEventListener('waiting', waiting)
      video.removeEventListener('seeking', seeking)
      document.removeEventListener('visibilitychange', resetClock)
      video.playbackRate = 1
      video.muted = originalMuted
    }
  }, [videoRef, source, speed])

  useEffect(() => {
    if (paused) videoRef.current?.pause()
  }, [paused, videoRef])

  return stepped
}
